/**
 * CohabitantsPanel — Mitbewohner (Kinder, Ehepartner o. Vertrag, Untermieter)
 * eines Mietverhältnisses. Werden u.a. auf Wohnungsgeberbestätigungen § 19 BMG
 * mit aufgeführt.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatDate } from "@/lib/constants";

export default function CohabitantsPanel({ tenancyId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/tenancies/${tenancyId}/cohabitants`);
      setItems(data || []);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => { if (tenancyId) load(); }, [tenancyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (c) => {
    if (!await window._domivusConfirm(`„${c.first_name} ${c.last_name}" wirklich entfernen?`)) return;
    try {
      await api.delete(`/tenancies/${tenancyId}/cohabitants/${c.id}`);
      toast.success("Entfernt");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-3" data-testid="cohabitants-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600 flex items-center gap-2">
          <Users size={16} className="text-slate-500" />
          Mitbewohner in der Wohnung (Kinder, Ehepartner o. Vertrag, Untermieter) — <strong>keine Vertragspartner</strong>.
          Werden in amtlichen Dokumenten (z. B. Wohnungsgeberbestätigung) mit aufgeführt.
        </div>
        <Button
          size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1.5"
          onClick={() => { setEditing(null); setOpen(true); }}
          data-testid="add-cohabitant-btn"
        >
          <Plus size={14} /> Mitbewohner hinzufügen
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Name</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Geburtsdatum</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Verhältnis</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Eingezogen</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-slate-500" data-testid="cohabitants-empty">Keine Mitbewohner erfasst.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`cohabitant-row-${c.id}`}>
                <td className="px-3 py-2 font-medium">{c.first_name} {c.last_name}</td>
                <td className="px-3 py-2 text-xs tabular-nums">{c.birth_date ? formatDate(c.birth_date) : "—"}</td>
                <td className="px-3 py-2 text-xs">{c.relationship || "—"}</td>
                <td className="px-3 py-2 text-xs tabular-nums">{c.moved_in_date ? formatDate(c.moved_in_date) : "—"}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(c); setOpen(true); }} data-testid={`edit-cohabitant-${c.id}`}>
                    <PencilSimple size={13} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600 hover:bg-rose-50" onClick={() => del(c)} data-testid={`delete-cohabitant-${c.id}`}>
                    <Trash size={13} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CohabitantDialog
        open={open}
        onOpenChange={setOpen}
        tenancyId={tenancyId}
        editing={editing}
        onSaved={() => { setOpen(false); load(); }}
      />
    </div>
  );
}

function CohabitantDialog({ open, onOpenChange, tenancyId, editing, onSaved }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", birth_date: "", relationship: "", moved_in_date: "", notes: "", contact_id: "",
  });
  // iter164b — Kontakt-Suche für Mitbewohner-Verlinkung
  const [contactQuery, setContactQuery] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [contactOpen, setContactOpen] = useState(false);
  useEffect(() => {
    if (!contactQuery || contactQuery.length < 2) { setContactResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", { params: { q: contactQuery, limit: 10 } });
        setContactResults(Array.isArray(data) ? data : (data?.items || []));
      } catch { /* silent */ }
    }, 200);
    return () => clearTimeout(t);
  }, [contactQuery]);

  useEffect(() => {
    if (editing) {
      setForm({
        first_name: editing.first_name || "",
        last_name: editing.last_name || "",
        birth_date: editing.birth_date || "",
        relationship: editing.relationship || "",
        moved_in_date: editing.moved_in_date || "",
        notes: editing.notes || "",
        contact_id: editing.contact_id || "",
      });
    } else {
      setForm({ first_name: "", last_name: "", birth_date: "", relationship: "", moved_in_date: "", notes: "", contact_id: "" });
    }
    setContactQuery(""); setContactResults([]); setContactOpen(false);
  }, [editing, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectContact = (c) => {
    setForm((f) => ({
      ...f,
      contact_id: c.id,
      first_name: f.first_name || c.first_name || "",
      last_name: f.last_name || c.last_name || c.company_name || "",
      birth_date: f.birth_date || c.birth_date || "",
    }));
    setContactOpen(false);
    setContactQuery("");
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      // leere Strings zu null (Backend akzeptiert Optional)
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (editing) {
        await api.patch(`/tenancies/${tenancyId}/cohabitants/${editing.id}`, payload);
        toast.success("Aktualisiert");
      } else {
        await api.post(`/tenancies/${tenancyId}/cohabitants`, payload);
        toast.success("Mitbewohner hinzugefügt");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Mitbewohner bearbeiten" : "Mitbewohner hinzufügen"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {/* iter164b — Kontakt-Verlinkung (optional) */}
          <div className="rounded-md border border-slate-200 bg-slate-50/70 p-2.5 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                Aus Kontakten wählen (optional)
              </Label>
              {form.contact_id && (
                <button
                  type="button"
                  className="text-[11px] text-rose-600 hover:underline"
                  onClick={() => setForm((f) => ({ ...f, contact_id: "" }))}
                  data-testid="cohab-unlink-contact"
                >
                  Verlinkung lösen
                </button>
              )}
            </div>
            {form.contact_id ? (
              <div className="text-xs text-slate-700 flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-full bg-teal-500" />
                Verlinkt: <b>{form.first_name} {form.last_name}</b>
              </div>
            ) : (
              <div className="relative">
                <Input
                  placeholder="Name oder Firma tippen…"
                  value={contactQuery}
                  onChange={(e) => { setContactQuery(e.target.value); setContactOpen(true); }}
                  onFocus={() => setContactOpen(true)}
                  data-testid="cohab-contact-search"
                />
                {contactOpen && contactResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
                    {contactResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => selectContact(c)}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0"
                        data-testid={`cohab-contact-hit-${c.id}`}
                      >
                        <div className="font-medium">
                          {c.first_name || ""} {c.last_name || c.company_name || ""}
                        </div>
                        {c.email && <div className="text-slate-500">{c.email}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Vorname *</Label>
              <Input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="cohab-first" />
            </div>
            <div>
              <Label className="text-xs">Nachname *</Label>
              <Input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="cohab-last" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Geburtsdatum</Label>
              <Input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} data-testid="cohab-birth" />
            </div>
            <div>
              <Label className="text-xs">Verhältnis</Label>
              <Input value={form.relationship} onChange={(e) => set("relationship", e.target.value)} placeholder="z. B. Kind, Ehefrau" data-testid="cohab-rel" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Eingezogen am</Label>
            <Input type="date" value={form.moved_in_date} onChange={(e) => set("moved_in_date", e.target.value)} data-testid="cohab-moved" />
          </div>
          <div>
            <Label className="text-xs">Notiz</Label>
            <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="cohab-notes" />
          </div>
          <DialogFooter>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="cohab-save-btn">
              {editing ? "Speichern" : "Hinzufügen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
